import React, { useCallback, useContext, useEffect } from 'react';
import { formatUnits } from 'ethers/lib/utils';
import { t } from 'i18next';
import { AmountInput, Button, ButtonStyle, ErrorBadge, ErrorLevel, Paragraph, Select, } from '@sovryn/ui';
import { RSK_CHAIN_ID } from '../../../../../../config/chains';
import { AssetRenderer } from '../../../../../2_molecules/AssetRenderer/AssetRenderer';
import { useChainStore } from '../../../../../../hooks/useChainStore';
import { translations } from '../../../../../../locales/i18n';
import { SendFlowContext, SendFlowStep } from '../../../contexts/sendflow';
import { useBridgeAggregatorBalance } from '../../../hooks/useBridgeAggregatorBalance';
import { useAssetsBySourceChain } from '../../../hooks/useBridgeAssets';
import { useERC20BridgeLocked } from '../../../hooks/useERC20BridgeLocked';
import { Limits } from '../../Limits';
import { NetworkRenderer } from '../../NetworkRenderer';
export const MainScreen = () => {
    const { set, token, chainId } = useContext(SendFlowContext);
    const assets = useAssetsBySourceChain(RSK_CHAIN_ID);
    const { data: aggregatorBalance } = useBridgeAggregatorBalance(RSK_CHAIN_ID, chainId, token);
    const { currentChainId, setCurrentChainId } = useChainStore();
    const isWrongChain = currentChainId !== RSK_CHAIN_ID;
    const isBridgeLocked = useERC20BridgeLocked();
    const uniqueAssets = assets.filter((asset, index, arr) => arr.findIndex(a => a.symbol === asset.symbol) === index);
    const filteredAssets = assets.filter(a => a.symbol === token);
    const chains = filteredAssets
        .filter(a => a.symbol === token)
        .map(a => a.sideChainId);
    const onContinueClick = useCallback(() => set(prevState => (Object.assign(Object.assign({}, prevState), { step: SendFlowStep.AMOUNT, amount: '' }))), [set]);
    const setAsset = useCallback((symbol) => {
        const asset = assets.find(a => a.symbol === symbol);
        set(prevState => (Object.assign(Object.assign({}, prevState), { token: asset === null || asset === void 0 ? void 0 : asset.symbol, chainId: asset === null || asset === void 0 ? void 0 : asset.sideChainId })));
    }, [assets, set]);
    const setChain = useCallback((network) => {
        const asset = filteredAssets.find(a => a.sideChainId === network);
        set(prevState => (Object.assign(Object.assign({}, prevState), { token: asset === null || asset === void 0 ? void 0 : asset.symbol, chainId: asset === null || asset === void 0 ? void 0 : asset.sideChainId })));
    }, [filteredAssets, set]);
    useEffect(() => {
        if (!token && !chainId && uniqueAssets.length > 0) {
            setAsset(uniqueAssets[0].symbol);
        }
    }, [chainId, setAsset, token, uniqueAssets]);
    return (React.createElement("div", null,
        React.createElement("div", { className: "mb-6" },
            React.createElement(Paragraph, { className: "mb-2 text-sm font-medium" }, "Asset"),
            React.createElement(Select, { className: "w-full", onChange: setAsset, options: uniqueAssets.map(asset => ({
                    value: asset.symbol,
                    label: (React.createElement(AssetRenderer, { showAssetLogo: true, asset: asset.symbol, chainId: asset.mainChainId, assetClassName: "font-medium" })),
                })), value: token || '' })),
        React.createElement("div", { className: "mb-6" },
            React.createElement(Paragraph, { className: "mb-2 text-sm font-medium" }, "Send to"),
            React.createElement(Select, { className: "w-full", onChange: setChain, options: chains
                    .filter(chain => !!chain)
                    .map(chainId => ({
                    value: chainId || '',
                    label: React.createElement(NetworkRenderer, { chainId: chainId }),
                })), value: chainId || '' })),
        React.createElement(AmountInput, { className: "w-full max-w-full mb-6", label: t(translations.erc20Bridge.confirmationScreens.avaiaibleLiquidity), value: formatUnits(aggregatorBalance || '0'), readOnly: true, decimalPrecision: 4, unit: token }),
        React.createElement(Limits, { sourceChain: RSK_CHAIN_ID, targetChain: chainId, asset: token }),
        isBridgeLocked ? (React.createElement(ErrorBadge, { level: ErrorLevel.Warning, message: t(translations.maintenanceMode.erc20Bridge) })) : isWrongChain ? (React.createElement(Button, { onClick: () => setCurrentChainId(RSK_CHAIN_ID), text: t(translations.erc20Bridge.confirmationScreens.switchNetwork), className: "w-full mt-12", style: ButtonStyle.secondary, dataAttribute: "switch-network-button", disabled: !token })) : (React.createElement(Button, { onClick: onContinueClick, text: t(translations.common.buttons.continue), className: "w-full mt-12", style: ButtonStyle.secondary, dataAttribute: "funding-send-instructions-confirm", disabled: isBridgeLocked ||
                !token ||
                !chainId ||
                !aggregatorBalance ||
                Number(aggregatorBalance) === 0 }))));
};
